import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  HeartPulse, LogOut, Stethoscope, Building2, Briefcase, TrendingUp,
  ShieldCheck, XCircle, CheckCircle2, Loader2, DollarSign, ListChecks
} from "lucide-react";

function Sidebar() {
  const { user, logout } = useAuth();
  const links = [
    { to: "/admin", label: "Dashboard", icon: TrendingUp, end: true, tid: "admin-nav-dashboard" },
    { to: "/admin/doctors", label: "Doctors", icon: Stethoscope, tid: "admin-nav-doctors" },
    { to: "/admin/hospitals", label: "Hospitals", icon: Building2, tid: "admin-nav-hospitals" },
    { to: "/admin/duties", label: "Duties", icon: Briefcase, tid: "admin-nav-duties" },
    { to: "/admin/payments", label: "Payments", icon: DollarSign, tid: "admin-nav-payments" },
  ];
  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-[#060D1A] text-white p-5 z-30">
      <div className="flex items-center gap-2 pb-6 border-b border-white/10">
        <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center"><ShieldCheck className="w-4 h-4" /></div>
        <div><div className="font-extrabold">MedShift</div><div className="text-[10px] uppercase tracking-widest text-red-300">Admin Console</div></div>
      </div>
      <nav className="mt-5 space-y-1 flex-1">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.tid}
            className={({isActive}) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${isActive ? "bg-white/10 text-white" : "text-sky-100/60 hover:bg-white/5"}`}>
            <l.icon className="w-4 h-4" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 pt-4">
        <div className="text-sm font-medium">{user?.name}</div>
        <div className="text-[10px] text-sky-300/70">{user?.email}</div>
        <Button size="sm" variant="outline" className="mt-3 w-full bg-transparent border-white/20 text-white hover:bg-white/10" onClick={logout} data-testid="admin-logout-btn">
          <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </aside>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({data}) => setStats(data)); }, []);
  if (!stats) return <div className="p-10"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  const kpis = [
    ["Doctors", stats.doctors, Stethoscope],
    ["Verified", stats.verified_doctors, ShieldCheck],
    ["Hospitals", stats.hospitals, Building2],
    ["Duties", stats.total_duties, Briefcase],
    ["Open", stats.open_duties, ListChecks],
    ["Filled", stats.filled_duties, CheckCircle2],
    ["Pending KYC", stats.pending_verifications, XCircle],
    ["Revenue (₹)", stats.revenue.toLocaleString(), DollarSign],
  ];
  return (
    <div className="p-8 space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight">Compliance & operations</h1>
        <p className="text-sm text-slate-500 mt-1">Fulfilment rate: <span className="font-mono font-bold text-emerald-600">{stats.fulfillment_rate}%</span></p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(([l, v, I]) => (
          <Card key={l} className="p-5 rounded-2xl med-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-slate-500">{l}</div>
                <div className="font-mono font-bold text-2xl mt-1">{v}</div>
              </div>
              <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"><I className="w-4 h-4" /></div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Doctors() {
  const [docs, setDocs] = useState([]);
  const load = () => api.get("/admin/doctors").then(({data}) => setDocs(data));
  useEffect(() => { load(); }, []);
  const verify = async (id, status) => {
    try { await api.post(`/admin/doctors/${id}/verify`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="p-8 space-y-4" data-testid="admin-doctors">
      <h1 className="text-3xl font-extrabold tracking-tight">Doctor verifications</h1>
      <Card className="rounded-2xl overflow-hidden">
        {docs.map(d => (
          <div key={d.user_id} className="flex items-center gap-4 p-4 border-b border-slate-100 flex-wrap" data-testid={`admin-doctor-${d.user_id}`}>
            <div className="w-11 h-11 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold">{d.full_name?.[0]}</div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold flex items-center gap-2">{d.full_name} <Badge className={`text-[10px] ${d.verification_status === "VERIFIED" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : d.verification_status === "PENDING" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-red-50 text-red-700 border border-red-200"}`}>{d.verification_status}</Badge></div>
              <div className="text-xs text-slate-500">{(d.specialties || []).join(", ")} · {d.experience_years}y · {d.medical_registration_no} · ⭐ {d.reliability_score}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-full" onClick={() => verify(d.user_id, "VERIFIED")} data-testid={`verify-doctor-${d.user_id}`}>Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => verify(d.user_id, "FAILED")}>Reject</Button>
              <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => verify(d.user_id, "SUSPENDED")}>Suspend</Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Hospitals() {
  const [hs, setHs] = useState([]);
  const load = () => api.get("/admin/hospitals").then(({data}) => setHs(data));
  useEffect(() => { load(); }, []);
  const verify = async (id, status) => {
    try { await api.post(`/admin/hospitals/${id}/verify`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="p-8 space-y-4" data-testid="admin-hospitals">
      <h1 className="text-3xl font-extrabold tracking-tight">Hospital approvals</h1>
      <Card className="rounded-2xl overflow-hidden">
        {hs.map(h => (
          <div key={h.user_id} className="p-4 border-b border-slate-100 flex items-center gap-4 flex-wrap">
            <div className="w-11 h-11 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold">{h.hospital_name?.[0]}</div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold flex items-center gap-2">{h.hospital_name} <Badge className={`text-[10px] ${h.verification_status === "VERIFIED" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{h.verification_status}</Badge></div>
              <div className="text-xs text-slate-500">{h.area} · {h.bed_capacity} beds · {h.icu_beds} ICU</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-full" onClick={() => verify(h.user_id, "VERIFIED")}>Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => verify(h.user_id, "SUSPENDED")}>Suspend</Button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Duties() {
  const [duties, setDuties] = useState([]);
  useEffect(() => { api.get("/admin/duties").then(({data}) => setDuties(data)); }, []);
  return (
    <div className="p-8 space-y-4" data-testid="admin-duties">
      <h1 className="text-3xl font-extrabold tracking-tight">Duty dispatch monitor</h1>
      <Card className="rounded-2xl overflow-hidden">
        <div className="grid grid-cols-6 gap-2 p-4 text-xs uppercase tracking-widest text-slate-500 font-mono border-b">
          <div>Hospital</div><div>Specialty</div><div>Date</div><div>Rate</div><div>Urgency</div><div>Status</div>
        </div>
        {duties.map(d => (
          <div key={d.duty_id} className="grid grid-cols-6 gap-2 p-4 text-sm border-b border-slate-100">
            <div className="font-medium">{d.hospital_name}</div>
            <div>{d.specialty}</div>
            <div>{d.date}</div>
            <div className="font-mono">₹{d.hourly_rate}</div>
            <div><Badge className={`text-[10px] ${d.urgency === "EMERGENCY" ? "bg-red-50 text-red-700 border border-red-200" : "bg-slate-50 border border-slate-200 text-slate-700"}`}>{d.urgency}</Badge></div>
            <div><Badge className="bg-white border border-slate-200 text-slate-700 text-[10px]">{d.status}</Badge></div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function Payments() {
  return (
    <div className="p-8" data-testid="admin-payments">
      <h1 className="text-3xl font-extrabold tracking-tight">Escrow & platform fees</h1>
      <Card className="mt-4 p-8 rounded-2xl text-center text-slate-500 text-sm">Escrow ledger view — Phase 2. Payouts release on hospital approval; platform fee is 10%.</Card>
    </div>
  );
}

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/");
    else if (user.role !== "admin") navigate("/dashboard");
  }, [user, loading, navigate]);
  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="lg:ml-64">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="doctors" element={<Doctors />} />
          <Route path="hospitals" element={<Hospitals />} />
          <Route path="duties" element={<Duties />} />
          <Route path="payments" element={<Payments />} />
        </Routes>
      </main>
    </div>
  );
}
